/**
 * External dependencies
 */
import React, { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector,
  Legend,
} from "recharts";
import { get, size } from "lodash";

/**
 * Internal dependencies
 */
import { COLORS } from "./constants";
import { capitalizeChartPivotData, formatMaybeDateString } from "./utils";
import EmptyContent from "./base/empty-content";

const renderActiveShape = (props) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;

  return (
    <g>
      <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill={fill}>
        {formatMaybeDateString(payload.name)}
      </text>
      <text x={cx} y={cy + 10} dy={8} textAnchor="middle">
        {`${value} (${(percent * 100).toFixed(2)}%)`}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};

const PieChartRenderer = ({
  resultSet,
  rowTotals,
  colorTheme,
  hideLegend,
  ...rest
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const onPieEnter = (data, index) => {
    setActiveIndex(index);
  };

  let data = capitalizeChartPivotData(resultSet.chartPivot());
  if (size(data) === 0) {
    return <EmptyContent />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart
        wrapperStyle={{ backgroundColor: "blue" }}
        margin={{ top: 25, right: 85, bottom: 25, left: 80 }}
      >
        {hideLegend ? null : (
          <Legend
            layout="vertical"
            verticalAlign="top"
            horizontalAlign="middle"
            align="right"
            wrapperStyle={{
              paddingLeft: "40px",
            }}
            iconType="circle"
            iconSize={10}
          />
        )}
        {resultSet.seriesNames().map((s, i) => (
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={data}
            paddingAngle={3}
            dataKey={s.key}
            onMouseEnter={onPieEnter}
            innerRadius="75%"
            outerRadius="95%"
            label={rowTotals}
          >
            {resultSet.chartPivot().map(({ color, ...rest }, index) => {
              return (
                <Cell
                  name={rest.x}
                  fill={get(COLORS, [colorTheme || "default", index])}
                  key={index}
                />
              );
            })}
          </Pie>
        ))}
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PieChartRenderer;
